import { cloneDeep } from 'lodash';
import { v4 as uuid } from 'uuid';

import { ExpenseFormValues } from '@ReactComponents/Shared/Expense/ExpenseEditor/IExpenseFormValues';
import { getMileageValue } from '@ReactComponents/Shared/Forms/MileageFields/MileageFields';

import { AbacusCurrency } from '@Shared/helpers';
import { ExpenseMetadata } from '@Shared/interfaces/ExpenseMetadata';
import { IExpenseModel, IImageModel } from '@Shared/models';

import { Api as apiService } from '../api';
import { IVendor } from './formFields';

export interface SplitSharedValues {
  amount: number;
  suggested_category_id?: string;
  entered_currency_amount: string;
  entered_currency_code: string;
  card_transaction_exchange_rate?: number;
  vendor: string;
  venue: IVendor;
  venueId?: string;
  metadata?: ExpenseMetadata;
  bypassWarnings?: boolean;
  card_transaction_id?: string;
  image_id?: string | null;
  image?: IImageModel | null;
  images?: IImageModel[] | null;
  user_id?: string;
  company_currency_amount?: number;
  company_currency_code?: string;
}

export type SplitBody = {
  shared: SplitSharedValues;
  splits: ExpenseFormValues[];
};

export abstract class SplitApi {
  static readonly splitTransactionsApiURL: string = '/api/web/split_transactions';

  static includeWithExpenses = [
    'include[user]',
    'include[user_profile]',
    'include[venue_info]',
    'include[image]',
    'include[images]',
    'include[category]',
    'include[expense_tags]',
    'include[metadata]',
    'include[manager]',
    'include[card_transaction]',
    'include[card]',
    'include[trip]'
  ].join('&');

  static createSplitExpense(splitBodyData: SplitBody, bypassWarnings?: boolean) {
    if (bypassWarnings) {
      splitBodyData.shared = { ...splitBodyData.shared, bypassWarnings: true };
    }

    const expenseData = {
      splits_data_string: encodeURIComponent(
        JSON.stringify({
          ...splitBodyData,
          bypassWarnings
        })
      )
    };

    return apiService
      .post(SplitApi.splitTransactionsApiURL, expenseData)
      .then((response) => response.json() as Promise<{ transactions: IExpenseModel[] }>);
  }

  static splitExistingExpense(splitBodyData: SplitBody, expense: IExpenseModel, bypassWarnings?: boolean) {
    (splitBodyData as any).shared = { ...expense, ...splitBodyData.shared, bypassWarnings: Boolean(bypassWarnings) };

    const expenseData = {
      splits_data_string: encodeURIComponent(
        JSON.stringify({
          ...splitBodyData,
          bypassWarnings
        })
      )
    };

    return apiService
      .post(`/api/web/transactions/${expense.id}/split`, expenseData)
      .then((response) => response.json() as Promise<{ transactions: IExpenseModel[] }>);
  }

  static deleteSplits(splitTransactionId: string): Promise<{ expenses: IExpenseModel[] }> {
    return apiService.responseToJSONAndToastError(apiService.delete(SplitApi.splitTransactionsApiURL + `/${splitTransactionId}`));
  }
}

export function splitExpense(baseValues?: ExpenseFormValues | IExpenseModel | null) {
  if (!baseValues) {
    return [];
  }

  const baseSplit1 = { ...cloneDeep(baseValues), key: uuid() };
  const baseSplit2 = { ...cloneDeep(baseValues), key: uuid() };

  // Set amount
  if (!(baseValues.metadata as any)?.mileage) {
    const currencyCode = baseValues.entered_currency_code || 'USD';
    const currencyAmountInMajor = Number(baseValues.entered_currency_amount);
    const [splitUsdAmount1, splitUsdAmount2] = new AbacusCurrency(baseValues.amount).split(2);

    const [splitCurrencyAmount1, splitCurrencyAmount2] = new AbacusCurrency(currencyAmountInMajor, currencyCode, true).split(2);

    baseSplit1.entered_currency_amount = String(splitCurrencyAmount1.getUnitValue());
    baseSplit2.entered_currency_amount = String(splitCurrencyAmount2.getUnitValue());
    baseSplit1.amount = splitUsdAmount1.getMinorUnitValue();
    baseSplit2.amount = splitUsdAmount2.getMinorUnitValue();
    baseSplit1.entered_currency_code = currencyCode;
    baseSplit2.entered_currency_code = currencyCode;

    if (baseSplit1.metadata && baseSplit2.metadata) {
      (baseSplit1.metadata as any).currency_amount = String(splitCurrencyAmount1.getUnitValue());
      (baseSplit2.metadata as any).currency_amount = String(splitCurrencyAmount2.getUnitValue());
    }

    if (baseValues.company_currency_amount !== undefined && baseValues.company_currency_code) {
      const [splitCompanyCurrencyAmount1, splitCompanyCurrencyAmount2] = new AbacusCurrency(
        baseValues.company_currency_amount,
        baseValues.company_currency_code
      ).split(2);
      baseSplit1.company_currency_amount = splitCompanyCurrencyAmount1.getMinorUnitValue();
      baseSplit2.company_currency_amount = splitCompanyCurrencyAmount2.getMinorUnitValue();
      baseSplit1.company_currency_code = baseValues.company_currency_code;
      baseSplit2.company_currency_code = baseValues.company_currency_code;
    }
  }

  // Set mileage
  if ((baseValues.metadata as any)?.mileage) {
    const mileageRateMinor = (baseValues.metadata as any).mileage_rate;

    const splitMileage = parseFloat(((baseValues.metadata as any).mileage / 2).toFixed(2));
    const mileageValue = getMileageValue(splitMileage, mileageRateMinor);
    baseSplit1.amount = mileageValue.amount;
    baseSplit1.entered_currency_amount = mileageValue.entered_currency_amount;
    baseSplit1.metadata = { ...baseSplit1.metadata, ...mileageValue.metadata };

    const remainingSplitMileage = (baseValues.metadata as any)?.mileage - splitMileage;
    const remainingMileageValue = getMileageValue(remainingSplitMileage, mileageRateMinor);
    baseSplit2.amount = remainingMileageValue.amount;
    baseSplit2.entered_currency_amount = remainingMileageValue.entered_currency_amount;
    baseSplit2.metadata = { ...baseSplit2.metadata, ...remainingMileageValue.metadata };
  }

  return [baseSplit1, baseSplit2] as ExpenseFormValues[];
}
